import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ChatIcon from '@mui/icons-material/Chat';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const features = [
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: '100% Real US Citizens',
      image: '/passport.jpg'
    },
    {
      icon: <FavoriteIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: <Typography variant="h6" component="h3" sx={{ color: 'white' }}>Real-ish Connections</Typography>,
      image: '/connection.png'
    },
    {
      icon: <ChatIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'ABGs Straight From the Source',
      image: '/abg.png'
    },
  ];
  

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      bgcolor: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
    }}>
      {/* Header */}
      <AppBar 
        position="fixed" 
        color="default" 
        elevation={1}
        sx={{ 
          backgroundColor: 'white',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src="/mylogo.png" 
              alt="Love is Green Logo" 
              style={{
                height: isMobile ? '100px' : '120px',
                width: 'auto',
                marginRight: '16px'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/login')}
              sx={{ 
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'rgba(33, 150, 243, 0.04)'
                }
              }}
            >
              Sign In
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              Register
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        pt: '64px', // Height of the AppBar
        background: 'linear-gradient(45deg,rgb(43, 131, 14) 30%,rgb(47, 243, 33) 90%)',
        color: 'white'
      }}>
        {/* Hero Section */}
        <Container maxWidth="lg" sx={{ flexGrow: 1 }}>
          <Box 
            sx={{ 
              pt: isMobile ? 8 : 15,
              pb: isMobile ? 3 : 4,
              textAlign: 'center'
            }}
          >
            <Typography 
              variant={isMobile ? "h3" : "h2"} 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                mb: 3
              }}
            >
              Find Your Perfect Match
            </Typography>
            
            </Box>
            {/* Centered Circular Image */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                my: 4,
                position: 'relative'
              }}
            >
              
              {/* Image Container */}
              <Box
                sx={{
                  margin: '0 auto',
                  width: { xs: '300', md: '350px' },
                  height: { xs: '350px', md: '350px' },
                  borderRadius: '10%',
                  overflow: 'hidden',
                  border: '2px solid white',
                  boxShadow: theme => `0 0 30px ${theme.palette.primary.main}`,
                  position: 'relative',
                  zIndex: 1,
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  backgroundColor: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: theme => `0 0 40px ${theme.palette.primary.main}`,
                  }
                }}
              >
                <img
                  src="/design.png"
                  alt="Happy Couple"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Box>
            
            
          {/* Features Section */}
          <Grid 
            container 
            spacing={4} 
            sx={{ 
              py: 8,
              px: isMobile ? 2 : 0
            }}
          >
            {features.map((feature, index) => (
              <Grid 
                item 
                xs={12}
                sm={6} 
                md={4} 
                key={index}
                sx={{
                  mb: isMobile ? 4 : 0
                }}
              >
                <Paper 
                  elevation={3}
                  sx={{ 
                    p: 4,
                    height: isMobile ? '250px' : '280px',
                    minHeight: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 8,
                      '& .feature-overlay': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${feature.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      zIndex: 1,
                    }
                  }}
                >
                  {/* Overlay */}
                  <Box
                    className="feature-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      transition: 'background-color 0.3s',
                      zIndex: 2,
                    }}
                  />
                  
                  {/* Content */}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    {feature.icon}
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      sx={{ 
                        mt: 2,
                        mb: 1,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        color: 'white',
                        flexGrow: 1,
                        opacity: 0.9
                      }}
                    >
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Typography variant="h4" sx={{ textAlign: 'center' }}>
            💥💥💥💥💥
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 3,
                textAlign: 'center'

              }}
            >
              Love is Green gets 5 big booms!
            </Typography>
            <Typography variant="h5" sx={{ textAlign: 'center' }}>
            - AJ and Big Justice
            </Typography>

          {/* Call to Action Section */}
          <Box 
            sx={{ 
              textAlign: 'center',
              py: 8
            }}
          >
            <Typography 
              variant="h4" 
              component="h2"
              sx={{ mb: 3 }}
            >
              Ready to Find Your Match?
            </Typography>
            <Typography 
              variant="h5"
              sx={{ 
                mb: 4,
                opacity: 0.9
              }}
            >
              Join our community of people looking for meaningful relationships (or a Green Card)
            </Typography>
            <Button 
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Join Now
            </Button>
          </Box>
        </Container>

        {/* Footer */}
        <Box 
          component="footer" 
          sx={{ 
            py: 4,
            bgcolor: 'rgba(0,0,0,0.1)',
            width: '100%',
          }}
        >
          <Container maxWidth="lg">
          <Typography 
              variant="body2" 
              align="center"
              sx={{ opacity: 0.8, color: 'white' }}
            >
              Contact us at <a href="mailto:info@loveisgreen.xyz" style={{ color: 'white' }}>info@loveisgreen.xyz</a>
            </Typography>
            <Typography 
              variant="body2" 
              align="center"
              sx={{ opacity: 0.8 }}
            >
              © {new Date().getFullYear()} Love Is Green
            </Typography>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage; 